/**
 * Deal-won / change-order bells include contract dollars.
 * Only executives should see them — keep in sync with `functions/src/dealWonNotify.ts`.
 */

export type DealWonNotifyViewer = {
  designations?: unknown;
  orgLevel?: string | null;
  role?: string | null;
  isActive?: boolean;
  isTestUser?: boolean;
  executiveBriefing?: { enabled?: boolean; profile?: string } | null;
  execInbox?: { enabled?: boolean } | null;
};

export const DEAL_WON_FINANCIAL_BELL_TYPES = new Set([
  'deal_won',
  'change_order_approved',
]);

function designationSet(raw: unknown): Set<string> {
  if (!Array.isArray(raw)) return new Set();
  return new Set(raw.map((d) => String(d).toLowerCase()));
}

/** CEO / COO / CFO / office managers / org-chart executives. Not EAs, designers, architects, or suppliers. */
export function isDealWonNotifyRecipient(user: DealWonNotifyViewer | null | undefined): boolean {
  if (!user) return false;
  if (user.isActive === false) return false;
  if (user.isTestUser === true) return false;
  const designations = designationSet(user.designations);
  if (designations.has('executive_assistant')) return false;
  if (designations.has('coo') || designations.has('cfo') || designations.has('office_manager')) return true;
  if (user.orgLevel === 'executive') return true;
  const briefing = user.executiveBriefing;
  if (briefing?.profile === 'ceo' || briefing?.profile === 'coo') return true;
  if (briefing?.enabled === true) return true;
  if (user.execInbox?.enabled === true) return true;
  return false;
}

export function filterDealWonBellsForUser<T extends { type?: string }>(
  items: T[],
  user: DealWonNotifyViewer | null | undefined,
): T[] {
  if (isDealWonNotifyRecipient(user)) return items;
  return items.filter((n) => !DEAL_WON_FINANCIAL_BELL_TYPES.has(String(n.type || '')));
}
